// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Persistent (IndexedDB) second-tier cache for the expensive raw analysis
// bundle ({ rawMetrics, engineDescriptors }) produced by
// computeRawAnalysisMetrics(). The in-memory rawAnalysisCache Map in app.js
// stays as the synchronous tier-1; this layer survives page reloads / app
// relaunches so a previously-viewed flight does not re-run the ~15-18s detector
// pass on every load.
//
// Caching is strictly an optimization: every entry point degrades silently to
// null/false on any failure (no IndexedDB, private browsing, serialization
// error, quota exceeded, ...), so the dashboard always falls back to recompute
// and flight loading never breaks. Mirrors the addEventListener-based IndexedDB
// pattern in flight-report-pdf.js.

import { ANALYSIS_VERSION } from "../core/score.js";

const ANALYSIS_CACHE_DB_NAME = "aeroInsightAnalysisCache";
const ANALYSIS_CACHE_STORE_NAME = "rawAnalysisBundles";
const ANALYSIS_CACHE_MAX_ENTRIES = 24;

// Permanently false for the session if a bundle ever fails structured clone
// (a future detector returning a function / class instance), so we stop paying
// the clone cost and stop logging DataCloneError on every write.
let cloneSupported = true;
// The explicit clone self-test only needs to pass once per session.
let cloneProven = false;

function openAnalysisCacheStore() {
  if (!globalThis.indexedDB) return Promise.resolve(null);
  return new Promise((resolve) => {
    let request;
    try {
      request = indexedDB.open(ANALYSIS_CACHE_DB_NAME, 1);
    } catch {
      resolve(null);
      return;
    }
    request.addEventListener("upgradeneeded", () => {
      const store = request.result.createObjectStore(ANALYSIS_CACHE_STORE_NAME, { keyPath: "key" });
      store.createIndex("lastAccess", "lastAccess");
    });
    request.addEventListener("success", () => resolve(request.result));
    request.addEventListener("error", () => resolve(null));
    request.addEventListener("blocked", () => resolve(null));
  });
}

async function withAnalysisCacheStore(mode, callback) {
  const database = await openAnalysisCacheStore();
  if (!database) return null;
  return new Promise((resolve) => {
    let transaction;
    try {
      transaction = database.transaction(ANALYSIS_CACHE_STORE_NAME, mode);
    } catch {
      database.close();
      resolve(null);
      return;
    }
    const store = transaction.objectStore(ANALYSIS_CACHE_STORE_NAME);
    const done = (value) => {
      database.close();
      resolve(value);
    };
    transaction.addEventListener("error", () => done(null));
    transaction.addEventListener("abort", () => done(null));
    callback(store, done);
  });
}

// Fire-and-forget LRU bump on read; failures are ignored.
function touchLastAccess(key) {
  withAnalysisCacheStore("readwrite", (store, done) => {
    const request = store.get(key);
    request.addEventListener("success", () => {
      const record = request.result;
      if (record) {
        record.lastAccess = Date.now();
        store.put(record);
      }
      done(true);
    });
    request.addEventListener("error", () => done(null));
  }).catch(() => {});
}

export async function readCachedRawAnalysisBundle(key) {
  if (!key) return null;
  const record = await withAnalysisCacheStore("readonly", (store, done) => {
    const request = store.get(key);
    request.addEventListener("success", () => done(request.result ?? null));
    request.addEventListener("error", () => done(null));
  }).catch(() => null);
  if (!record || record.version !== ANALYSIS_VERSION) return null;
  const bundle = record.bundle;
  if (!bundle || typeof bundle !== "object" || typeof bundle.rawMetrics !== "object") return null;
  touchLastAccess(key);
  return bundle;
}

// Evict oldest entries (by lastAccess) until at most MAX remain. Runs inside the
// same readwrite transaction as the preceding put.
function pruneToMaxEntries(store, done) {
  const countRequest = store.count();
  countRequest.addEventListener("success", () => {
    const overflow = countRequest.result - ANALYSIS_CACHE_MAX_ENTRIES;
    if (overflow <= 0) {
      done(true);
      return;
    }
    let removed = 0;
    const cursorRequest = store.index("lastAccess").openCursor();
    cursorRequest.addEventListener("success", () => {
      const cursor = cursorRequest.result;
      if (!cursor || removed >= overflow) {
        done(true);
        return;
      }
      cursor.delete();
      removed += 1;
      cursor.continue();
    });
    cursorRequest.addEventListener("error", () => done(null));
  });
  countRequest.addEventListener("error", () => done(null));
}

export async function writeCachedRawAnalysisBundle(key, flightId, bundle) {
  if (!key || !bundle || typeof bundle !== "object") return false;
  if (!cloneSupported) return false;
  // Prove (once) the bundle survives structured clone before persisting; this
  // converts a would-be DataCloneError on store.put into a one-time, silent
  // opt-out for the session.
  if (!cloneProven) {
    try {
      if (typeof structuredClone === "function") structuredClone(bundle);
      cloneProven = true;
    } catch {
      cloneSupported = false;
      return false;
    }
  }
  const record = {
    key,
    flightId: flightId || "",
    bundle,
    version: ANALYSIS_VERSION,
    createdAt: new Date().toISOString(),
    lastAccess: Date.now(),
  };
  const saved = await withAnalysisCacheStore("readwrite", (store, done) => {
    let putRequest;
    try {
      putRequest = store.put(record);
    } catch {
      // Synchronous DataCloneError (defensive; the self-test above should have
      // already caught a non-cloneable bundle).
      cloneSupported = false;
      done(null);
      return;
    }
    putRequest.addEventListener("success", () => pruneToMaxEntries(store, done));
    putRequest.addEventListener("error", () => done(null));
  }).catch(() => null);
  return Boolean(saved);
}

// Drop every persisted bundle belonging to a flight, regardless of
// ANALYSIS_VERSION (a stale bundle from an older version still pins the
// overview tiles until it is evicted). The object store has no flightId index,
// so we cursor-scan the ≤24 records. Returns the number of records removed;
// degrades silently to 0 on any failure.
export async function deleteCachedRawAnalysisBundlesForFlight(flightId) {
  if (!flightId) return 0;
  const removed = await withAnalysisCacheStore("readwrite", (store, done) => {
    let count = 0;
    const cursorRequest = store.openCursor();
    cursorRequest.addEventListener("success", () => {
      const cursor = cursorRequest.result;
      if (!cursor) {
        done(count);
        return;
      }
      if (cursor.value?.flightId === flightId) {
        cursor.delete();
        count += 1;
      }
      cursor.continue();
    });
    cursorRequest.addEventListener("error", () => done(count));
  }).catch(() => 0);
  return removed ?? 0;
}
